import React, {useEffect, useMemo, useState} from 'react'
import type {ICurriculum, IStory, ID} from '../../constants'
import {UiIcon} from '../../components/UiIcon'
import {curriculumGrade, curriculumGradeLabel, sortCurriculums} from './curriculumOrder'
import '../story/StoryCatalog.scss'
import './CurriculumLibraries.scss'

interface Props {
  curriculums: ICurriculum[]
  stories: IStory[]
  activeCurriculumId?: ID
  loading?: boolean
  error?: boolean
  onRetry: () => void
  onOpenStory: (story: IStory) => void
}

function LibraryBook({story, onOpen}: {story: IStory; onOpen: () => void}) {
  const [failed, setFailed] = useState(false)
  const cover = `${story.basePath || ''}${story.fileProfiles?.[0]?.filePath || ''}${
    story.thumbnail || ''
  }`.replace(/file\.kidly\.world/g, 'files.gokidly.com')
  useEffect(() => setFailed(false), [cover])
  return (
    <article className='edu-book-card'>
      <button
        type='button'
        className='edu-book-cover'
        onClick={onOpen}
        aria-label={`${story.title}: kitap detayını aç`}
      >
        {!story.thumbnail || failed ? (
          <span className='edu-book-fallback'>
            <UiIcon name='book' size={36} />
            <span>Kapak görseli yok</span>
          </span>
        ) : (
          <img src={cover} alt='' loading='lazy' onError={() => setFailed(true)} />
        )}
      </button>
      <div className='edu-book-info'>
        <h3 title={story.title}>{story.title}</h3>
        <p>{story.author || 'Türkçe kitap'}</p>
        <button
          type='button'
          className='edu-book-select'
          onClick={onOpen}
          aria-label={`${story.title}: kitabı incele`}
        >
          Kitabı incele <UiIcon name='arrow-right' size={15} />
        </button>
      </div>
    </article>
  )
}

export function CurriculumLibraries({
  curriculums,
  stories,
  activeCurriculumId,
  loading,
  error,
  onRetry,
  onOpenStory,
}: Props) {
  const sorted = useMemo(() => sortCurriculums(curriculums), [curriculums])
  const defaultId =
    sorted.find((item) => String(item.id) === String(activeCurriculumId))?.id ?? sorted[0]?.id
  const [openId, setOpenId] = useState<string | null>(defaultId == null ? null : String(defaultId))
  useEffect(() => {
    setOpenId(defaultId == null ? null : String(defaultId))
  }, [defaultId])
  const byId = useMemo(
    () =>
      new Map(
        stories
          .filter((story) => story.languageCode === 'tr-TR')
          .map((story) => [String(story.storyId), story])
      ),
    [stories]
  )

  if (loading)
    return (
      <div className='edu-libraries-state' role='status'>
        <UiIcon name='book' size={32} />
        <h2>Kitaplıklarınız hazırlanıyor</h2>
        <p>Sınıf düzeylerine göre kitapları bir araya getiriyoruz.</p>
      </div>
    )
  if (error)
    return (
      <div className='edu-libraries-state' role='alert'>
        <UiIcon name='info' size={32} />
        <h2>Kitaplıklar yüklenemedi</h2>
        <p>Bağlantınızı kontrol edip yeniden deneyin.</p>
        <button className='btn btn-primary' onClick={onRetry}>
          Tekrar dene
        </button>
      </div>
    )
  if (!sorted.length)
    return (
      <div className='edu-libraries-state'>
        <UiIcon name='book' size={32} />
        <h2>Henüz bir kitaplık yok</h2>
        <p>Size tanımlanan kitaplıklar burada görünecek.</p>
      </div>
    )

  return (
    <div className='edu-libraries'>
      <div className='edu-libraries-summary'>
        <div>
          <UiIcon name='book' size={18} />
          <strong>{sorted.length} kitaplık</strong>
          <span>Sınıf düzeyine göre sıralı</span>
        </div>
        <p>Kitapları görmek için bir kitaplık açın.</p>
      </div>
      {sorted.map((item) => {
        const open = openId === String(item.id)
        const active = String(item.id) === String(activeCurriculumId)
        const ids = Array.from(
          new Set((item.curriculumDetail || []).map((detail) => String(detail.storyId)))
        )
        const books = ids.map((id) => byId.get(id)).filter((story): story is IStory => !!story)
        const grade = curriculumGrade(item)
        return (
          <section
            key={item.id}
            className={`edu-library${open ? ' is-open' : ''}${active ? ' is-current' : ''}`}
          >
            <h2 className='edu-library-heading'>
              <button
                type='button'
                className='edu-library-toggle'
                id={`library-heading-${item.id}`}
                aria-expanded={open}
                aria-controls={`library-books-${item.id}`}
                onClick={() => setOpenId(open ? null : String(item.id))}
              >
                <span
                  className={`edu-library-grade${grade === 0 ? ' is-preschool' : ''}`}
                  aria-hidden='true'
                >
                  {grade === 0 || grade == null ? (
                    <UiIcon name='book' size={24} />
                  ) : (
                    <>
                      {grade}
                      <small>. sınıf</small>
                    </>
                  )}
                </span>
                <span className='edu-library-title'>
                  <span className='edu-library-level'>{curriculumGradeLabel(item)}</span>
                  <strong>{item.title}</strong>
                </span>
                <span className='edu-library-meta'>
                  {active && (
                    <span className='edu-library-current'>
                      <UiIcon name='check' size={14} />
                      Sınıfınızın kitaplığı
                    </span>
                  )}
                  <span>{ids.length} kitap</span>
                </span>
                <span className='edu-library-chevron'>
                  <UiIcon name='chevron-down' size={20} />
                </span>
              </button>
            </h2>
            <div
              id={`library-books-${item.id}`}
              role='region'
              aria-labelledby={`library-heading-${item.id}`}
              hidden={!open}
            >
              {open && (
                <>
                  {books.length > 0 ? (
                    <div className='edu-book-grid'>
                      {books.map((story) => (
                        <LibraryBook
                          key={story.storyId}
                          story={story}
                          onOpen={() => onOpenStory(story)}
                        />
                      ))}
                    </div>
                  ) : (
                    <p className='edu-library-empty'>
                      {ids.length
                        ? 'Bu kitaplığın kitap bilgileri şu anda görüntülenemiyor.'
                        : 'Bu kitaplığa henüz kitap eklenmemiş.'}
                    </p>
                  )}
                  {books.length > 0 && books.length < ids.length && (
                    <p className='edu-library-empty'>
                      {ids.length - books.length} kitabın bilgisi şu anda görüntülenemiyor.
                    </p>
                  )}
                </>
              )}
            </div>
          </section>
        )
      })}
    </div>
  )
}
